import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { hookUtils } from '@indico-data/utils';
import { Button } from 'Permafrost/index';
import { Icon } from 'Permafrost/index';
import { StyledFilterSelect } from './FilterSelect.styles';
import { isEqual } from 'lodash';
import { IconName } from 'Permafrost/index';
import { FilterSelectControls, FilterSelectOptions, FilterSelectReset } from './components';
import { sortOptionsList } from './helpers';

export type FilterOption = {
  text: string;
  value: string;
  iconName?: IconName;
};

export type FilterProps = {
  isMulti?: boolean;
  label?: string;
  optionsList: FilterOption[];
  placement?: 'left' | 'right';
  selected: FilterOption[];
  onSave(options: FilterOption[]): void;
};

export const FilterSelect = ({
  isMulti = false,
  label,
  optionsList,
  placement = 'right',
  selected,
  onSave,
}: FilterProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterOption[]>([]);
  const menuEl = useRef() as React.MutableRefObject<HTMLDivElement>;

  const hasSelected = selectedFilters?.length > 0;

  const handleToggleMultiFilter = (option: FilterOption) => {
    if (selectedFilters?.some((filterOption) => isEqual(filterOption.value, option.value))) {
      setSelectedFilters(
        selectedFilters?.filter((filterOption) => !isEqual(filterOption.value, option.value))
      );
    } else {
      setSelectedFilters([...selectedFilters, option]);
    }
  };

  const handleToggleSingleFilter = (option: FilterOption) => {
    isEqual(selectedFilters?.[0], option) && hasSelected
      ? setSelectedFilters([])
      : setSelectedFilters([option]);
  };

  const handleOptionClick = (option: FilterOption) => {
    isMulti ? handleToggleMultiFilter(option) : handleToggleSingleFilter(option);
  };

  hookUtils.useClickOutside(menuEl, () => {
    if (isOpen) {
      setSelectedFilters(selected ? selected : []);
      setIsOpen(false);
    }
  });

  // ensures local state of selectedFilters is updated when selected prop is changed
  useEffect(() => {
    if (selected && selected !== selectedFilters) setSelectedFilters(selected);
  }, [selected]);

  return (
    <StyledFilterSelect ref={menuEl} $placement={placement} className="filter">
      <Button
        className={classNames('FilterSelect__toggle', 'square', {
          'filters-on': hasSelected,
        })}
        data-testid="toggle-btn"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      >
        {label ? <span className="filter-label">{label}</span> : null}
        <Icon name={label ? 'fa-caret-down' : 'filter'} />
        {hasSelected ? <span className="swatch-circle" /> : []}
      </Button>
      <div
        data-testid="div-filter-options-container"
        className={classNames('filter__container', {
          visible: isOpen,
        })}
      >
        <div className="FilterSelect__options">
          <FilterSelectOptions
            isMulti={isMulti}
            optionsList={sortOptionsList(optionsList, selected)}
            selectedFilters={selectedFilters}
            handleOptionClick={handleOptionClick}
          />
          <FilterSelectReset
            selectedFilterCount={selectedFilters.length}
            setSelectedFilters={setSelectedFilters}
          />
        </div>
        <FilterSelectControls
          currentStoreFilters={selected}
          selectedFilters={selectedFilters}
          onSave={onSave}
          setIsOpen={setIsOpen}
          setSelectedFilters={setSelectedFilters}
        />
      </div>
    </StyledFilterSelect>
  );
};
